import React, {useEffect, useState} from 'react';

import cs from 'classnames';
import toPairs from 'lodash/toPairs';
import {Modal, ModalBody, ModalHeader} from 'reactstrap';
import styled from 'styled-components';

import {HeadingText} from 'app/components/general/HeadingText';
import {gt} from 'app/utils/i18n';

import {
  categories,
  KeyboardShortcutsContext,
  RegisteredShortcutType,
} from './KeyboardShortcutsProvider';
import {ShortcutKey} from './ShortcutKey';
import {cats, withShortcuts, WithShortcutsProps} from './withShortcuts';

function BaseKeyboardShortcutsHelpModal({enableShortcut}: WithShortcutsProps) {
  const [mounted, setMounted] = useState(false);
  const [isOpen, setOpen] = useState(false);

  const toggle = () => {
    if (!mounted) {
      setMounted(true);
    }

    setOpen((open) => !open);
  };

  useEffect(() => {
    enableShortcut('?', toggle);
  }, []);

  function getCategorizedShortcuts(
    cats: Array<string>,
    shortcuts: Record<string, RegisteredShortcutType>
  ) {
    return categories
      .filter((c) => cats.indexOf(c.slug) > -1)
      .map((cat) => ({
        ...cat,
        items: toPairs(shortcuts).filter(
          (shortcut) => shortcut[1].category === cat.slug
        ),
      }));
  }

  function renderCategories(
    cats: Array<string>,
    shortcuts: Record<string, RegisteredShortcutType>
  ) {
    return getCategorizedShortcuts(cats, shortcuts).map((cat) => (
      <dl key={cat.slug}>
        <dt>{cat.name()}</dt>
        <dd>
          {cat.items.map(([key, {name, enabled}]) => (
            <StyledShortcutsListDiv
              key={key}
              className="d-flex my-1 align-items-center"
            >
              <div className="key-container">
                <StyledKeyDecoratorDiv
                  className={cs({
                    enabled,
                  })}
                >
                  <ShortcutKey>{key}</ShortcutKey>
                </StyledKeyDecoratorDiv>
              </div>
              <div className="title">{name()}</div>
            </StyledShortcutsListDiv>
          ))}
        </dd>
      </dl>
    ));
  }

  // defer the mounting of context-consumer and modal until user press ?
  if (!mounted) {
    return null;
  }

  return (
    <KeyboardShortcutsContext.Consumer>
      {({shortcuts}) => (
        <Modal keyboard={true} isOpen={isOpen} toggle={toggle} size="lg">
          <ModalHeader toggle={toggle}>
            <HeadingText size="0.9rem">{gt('Keyboard Shortcuts')}</HeadingText>
          </ModalHeader>
          <ModalBody>
            <div className="row">
              <div className="col-12 col-sm-6">
                {renderCategories(['everywhere'], shortcuts)}
              </div>
              <div className="col-12 col-sm-6">
                {renderCategories(['detailPage', 'editPage'], shortcuts)}
              </div>
            </div>
            <hr />
            <StyledShortcutsListDiv>
              <div className="d-inline-block mr-4">
                <StyledKeyDecoratorDiv className="enabled mr-2">
                  <ShortcutKey>&nbsp;</ShortcutKey>
                </StyledKeyDecoratorDiv>
                <span className="small">
                  {gt('Shortcut available in current page')}
                </span>
              </div>
              <div className="d-inline-block">
                <StyledKeyDecoratorDiv className="mr-2">
                  <ShortcutKey>&nbsp;</ShortcutKey>
                </StyledKeyDecoratorDiv>
                <span className="small">
                  {gt('Shortcut available in other pages')}
                </span>
              </div>
            </StyledShortcutsListDiv>
          </ModalBody>
        </Modal>
      )}
    </KeyboardShortcutsContext.Consumer>
  );
}

export const KeyboardShortcutsHelpModal = withShortcuts({
  key: '?',
  name: () => 'Open this help modal',
  category: cats.everywhere,
})(BaseKeyboardShortcutsHelpModal);

const StyledKeyDecoratorDiv = styled.div`
    display: inline-block;
    border-radius: 4px;
    border 4px solid transparent;

    &.enabled {
        border-color: #93effb;
    }
`;

const StyledShortcutsListDiv = styled.div`
  .key-container {
    width: 50px;
    margin-right: 10px;
    margin-left: 10px;
    text-align: right;
    flex-shrink: 0;
  }
`;
